import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PlusCircle } from 'lucide-react'

import { HotelReservationViewer } from '@/features/reservation-viewer/ui/HotelReservationViewer'
import { type CheckoutChange, useCheckoutChanges } from '@/hooks/use-checkout-changes'
import { useClientSearch } from '@/shared/hooks/use-client-search'
import { Button } from '@/shared/ui/button'
import { ClientSearchModal } from '@/shared/ui/client-search-modal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs.tsx'
import { CheckoutChangesNotificationBanner } from '@/widgets/HairSalonCalendar/ui/CheckoutChangesNotificationBanner'
import { HairSalonCalendarTab } from '@/widgets/HairSalonCalendar/ui/HairSalonCalendarTab'
import { HairSalonPendingReservationsTab } from '@/widgets/HairSalonCalendar/ui/HairSalonPendingReservationsTab'
import { HairSalonReservationViewer } from '@/widgets/HairSalonCalendar/ui/HairSalonReservationViewer'
import { Reservation } from '@monorepo/functions/src/types/reservations'

export default function HairSalonInternalPanelPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    //const { updateReservation: updateCalendarReservation } = useCalendarStore()
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null)

    // Usar el hook para manejar los cambios de checkout
    const { checkoutChanges, removeCheckoutChange } = useCheckoutChanges()
    const [selectedChange, setSelectedChange] = useState<CheckoutChange | null>(null)

    // Get the active tab from the URL
    const activeTab = searchParams.get('view') || 'pending'
    const managingReservationId = searchParams.get('reservationId')

    // Initialize URL params only once on mount
    useEffect(() => {
        const view = searchParams.get('view')
        if (!view) {
            const newParams = new URLSearchParams(searchParams)
            newParams.set('view', 'pending')
            setSearchParams(newParams)
        }
    }, [])

    const handleTabChange = (value: string) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev)
            newParams.set('view', value)
            if (value === 'pending') {
                newParams.delete('reservationId')
            }
            return newParams
        })
    }

    const handleOrganizeCita = (reservationId: string) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev)
            newParams.set('view', 'calendar')
            newParams.set('reservationId', reservationId)
            return newParams
        })
    }

    const handleViewChange = (change: CheckoutChange) => {
        handleOrganizeCita(change.reservationId)
    }

    const { isOpen, handleOpen, handleClose, handleClientSelect, redirectPath, requirePetSelection } = useClientSearch({
        onClientSelect: (clientId, petIndex) => {
            const params = new URLSearchParams()
            params.set('userId', clientId)
            if (typeof petIndex === 'number') {
                params.set('petIndex', String(petIndex))
            }
            window.location.href = `/peluqueria-booking?${params.toString()}`
        },
        redirectPath: '/peluqueria-booking',
        requirePetSelection: true,
    })

    return (
        <div className='p-4 md:p-6'>
            <div className='mb-4 flex items-center justify-between'>
                <h1 className='text-3xl font-bold'>Peluquería</h1>
                <Button className='bg-dondersteen text-white hover:bg-dondersteen/90' onClick={handleOpen}>
                    <PlusCircle className='mr-2 h-4 w-4' /> Nueva Reserva
                </Button>
            </div>
            {isOpen && (
                <ClientSearchModal
                    isOpen={isOpen}
                    onClose={handleClose}
                    onClientSelect={handleClientSelect}
                    redirectPath={redirectPath}
                    requirePetSelection={requirePetSelection}
                />
            )}

            {/* Banner de notificaciones de cambios de checkout */}
            <CheckoutChangesNotificationBanner
                changes={checkoutChanges}
                onViewChange={handleViewChange}
                onAcknowledgeChange={change => removeCheckoutChange(change.reservationId)}
            />

            <div className='mt-6'>
                <Tabs value={activeTab} onValueChange={handleTabChange} className='space-y-6'>
                    <TabsList className='grid w-full grid-cols-2 gap-4 bg-transparent p-0'>
                        <TabsTrigger
                            value='pending'
                            className='relative flex items-center justify-center gap-2 border bg-white shadow-sm hover:bg-gray-50/80 data-[state=active]:border-[#4B6BFB] data-[state=active]:bg-[#4B6BFB] data-[state=active]:text-white'
                        >
                            Reservas pendientes
                        </TabsTrigger>
                        <TabsTrigger
                            value='calendar'
                            className='relative flex items-center justify-center gap-2 border bg-white shadow-sm hover:bg-gray-50/80 data-[state=active]:border-[#4B6BFB] data-[state=active]:bg-[#4B6BFB] data-[state=active]:text-white'
                        >
                            Calendario
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value='pending' className='mt-6'>
                        <HairSalonPendingReservationsTab
                            onOrganizeCita={reservation => handleOrganizeCita(reservation.id)}
                        />
                    </TabsContent>

                    <TabsContent value='calendar'>
                        <HairSalonCalendarTab managingReservationId={managingReservationId} />
                    </TabsContent>
                </Tabs>
            </div>

            {/* Modal de detalles de reserva */}
            {selectedReservation &&
                !selectedChange &&
                (selectedReservation.type === 'hotel' ? (
                    <HotelReservationViewer
                        reservation={selectedReservation}
                        isOpen={!!selectedReservation}
                        onClose={() => {
                            setSelectedReservation(null)
                            setSelectedChange(null)
                        }}
                    />
                ) : (
                    <HairSalonReservationViewer
                        reservationId={selectedReservation.id}
                        isOpen={!!selectedReservation}
                        onClose={() => {
                            setSelectedReservation(null)
                            setSelectedChange(null)
                        }}
                    />
                ))}
        </div>
    )
}
